import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { CANVAS_NOTE_FONTS } from '../utils/canvasFonts'
import type { CanvasOptionsBarPlacement } from './CanvasOptionsBar.types'

export interface CanvasFontMenuProps {
  /** Die aktuelle Schrift; `undefined`, wenn die Auswahl uneinheitlich ist. */
  value: string | null | undefined
  placement: CanvasOptionsBarPlacement
  /**
   * Der Aufklapper wird VON AUSSEN gemessen — die Leiste prüft, ob er in die
   * Richtung passt, in die sie zeigt. Ab React 19 genügt dafür eine schlichte
   * Prop, kein `forwardRef`.
   */
  ref?: React.Ref<HTMLDivElement>
  onPick: (key: string | null) => void
  /**
   * Der Zeiger steht auf einem Eintrag (`{ font }`, `null` = Standard) oder
   * hat das Menü verlassen (`null`). Zwischen zwei Einträgen kommt KEIN
   * `null` — das Element spränge sonst auf dem Weg nach unten zurück. Das
   * Schließen meldet die Leiste.
   */
  onPreview?: (choice: { font: string | null } | null) => void
  /** Blendet gerade ab (siehe `usePresence`) — als `data-leaving`, die SCSS spielt rückwärts. */
  leaving?: boolean
}

/**
 * Das **Schriftmenü** — acht Einträge, jeder in seiner eigenen Schrift gesetzt
 * (Figma `5083:51851`). Das Menü IST die Vorschau: Was hier steht, kommt auf
 * dem Zettel genauso heraus, deshalb auch derselbe Schnitt.
 *
 * An der linken Kante seines Knopfes ausgerichtet und nicht zentriert — es ist
 * breiter als der Knopf.
 *
 * Bei uneinheitlicher Auswahl steht **kein** Häkchen: Es gibt dann keine
 * Schrift, die für alle gilt.
 *
 * **Beim Überfahren trägt das Element die Schrift schon** (`onPreview`) —
 * dasselbe Muster wie beim Farbwähler.
 */
export const CanvasFontMenu: React.FC<CanvasFontMenuProps> = ({ value, placement, onPick, onPreview, ref, leaving = false }) => {
  const { t } = useTranslation()

  return (
    <div
      ref={ref}
      className={`canvas-options-bar__flyout canvas-options-bar__flyout--start canvas-options-bar__flyout--${placement}`}
      data-leaving={leaving ? '' : undefined}
      role="listbox"
      aria-label={t('projectCanvas.note.changeFont')}
      onMouseLeave={() => onPreview?.(null)}
    >
      {CANVAS_NOTE_FONTS.map(font => {
        const selected = value !== undefined && (font.key ?? null) === (value ?? null)
        return (
          <button
            key={font.key ?? 'default'}
            type="button"
            role="option"
            aria-selected={selected}
            className="canvas-options-bar__font-item"
            /*
              Jeder Eintrag steht in seiner Schrift UND in ihrem optischen
              Ausgleich: Das Menü ist die Vorschau, und eine Vorschau, in der
              Caveat halb so groß steht wie Lora, verspricht das Falsche.
            */
            style={{
              fontFamily: font.stack,
              fontWeight: font.weight,
              fontSize: `calc(14px * ${font.scale})`,
            }}
            onMouseEnter={() => onPreview?.({ font: font.key ?? null })}
            onClick={() => onPick(font.key)}
          >
            {selected && <Check className="canvas-options-bar__font-check" size={16} strokeWidth={2} />}
            {font.label}
          </button>
        )
      })}
    </div>
  )
}
